import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { string } from 'yup';
import DetailsCard from '../../components/DetailsCard/DetailsCard';
import FormatPhone from '../../components/FormatNumber/FormatPhone';
import DefaultModal from '../../components/Modals/DefaultModal';
import SuccessModal from '../../components/Modals/SuccessModal';
import PageTitle from '../../components/PageTitle/PageTitle';
import TwoColumnsText from '../../components/TwoColumnsText/TwoColumnsText';
import { dashBorder } from '../../constants';
import { useUserRoles } from '../../hooks/useUserRoles';
import { Profile as ProfileModel } from '../../models/profile';
import { userService } from '../../services/userService';
import ChangePassword from './ChangePassword';
import ContactInformationForm from './ProfileForms/ContactInformationForm';

const BoldText = styled(Typography)(() => ({
  fontWeight: 'bold',
  display: 'flex'
}));

const LinkStyle = styled(Link)(() => ({
  color: '#0060C9',
  textDecoration: 'none',
  cursor: 'pointer',
  display: 'inline'
}));

const schema = yup.object({
  firstName: string().default('').required('First Name is required'),
  lastName: string().default('').required('Last Name is required'),
  address: string().default(null).nullable(),
  city: string().default(''),
  state: string().default('').typeError('State is required'),
  country: string().default('USA'),
  zipCode: string().default('').typeError('Zip Code is required'),
  phoneNumber: string().default(''),
  email: string().default('').email().required('Email is required')
});

const ProfileDataCols = [
  'First Name',
  'Last Name',
  'Address',
  'City',
  'State',
  'Country',
  'Zip Code',
  'Email',
  'Phone'
];

const Profile = (): JSX.Element => {
  const [user, setUser] = useState<ProfileModel | undefined>(undefined);
  const [openChangePassModal, setOpenChangePassModal] = useState(false);
  const [isEditingContactInfo, setIsEditingContactInfo] = useState(false);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [profileDataLeft, setProfileDataLeft] = useState<
    {
      label: string;
      value: string | number | React.ReactNode;
      render?: React.ReactNode;
    }[]
  >([
    { label: 'First Name', value: '--' },
    { label: 'Last Name', value: '--' },
    { label: 'Address', value: '--' },
    { label: 'City', value: '--' },
    { label: 'State', value: '--' },
    { label: 'Country', value: '--' },
    { label: 'Zip Code', value: '--' },
    { label: 'Email', value: '--' },
    { label: 'Phone', value: '--' }
  ]);
  const [profileDataRight, setProfileDataRight] = useState([
    { label: 'Email', value: '--' },
    { label: 'Password', value: '***********' }
  ]);
  const { keycloak } = useKeycloak();
  const userData = keycloak.tokenParsed;
  const { isTalent } = useUserRoles();

  const methods = useForm<ProfileModel>({
    resolver: yupResolver(schema)
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    getUserData();
  }, []);

  const onSubmit: SubmitHandler<ProfileModel> = (data) => {
    userService.updateCurrentProfile(data).then(() => {
      setOpenSuccessModal(true);
    });
  };

  const getUserData = () => {
    userService.getProfileData().then((response) => {
      const user = response.data;
      setUser(response.data);
      setProfileDataLeft([
        { label: 'First Name', value: user.firstName ?? '--' },
        { label: 'Last Name', value: user.lastName ?? '--' },
        { label: 'Address', value: user.address ?? '--' },
        { label: 'City', value: user.city ?? '--' },
        { label: 'State', value: user.state ?? '--' },
        {
          label: 'Zip Code',
          value: user.zipCode ? String(user.zipCode) : '--'
        },
        { label: 'Email', value: user.email },
        {
          label: 'Phone',
          value: user.phoneNumber ? (
            <FormatPhone value={user?.phoneNumber} />
          ) : (
            '--'
          )
        }
      ]);

      setProfileDataRight([
        { label: 'Email', value: user.email ?? '--' },
        { label: 'Password', value: '***********' }
      ]);

      reset(response.data);
    });
  };

  const handleClose = () => {
    setIsEditingContactInfo(false);
    reset(user);
  };

  const closeSuccessModal = () => {
    setOpenSuccessModal(false);
    setIsEditingContactInfo(false);
    getUserData();
  };

  return (
    <>
      <PageTitle>My Profile</PageTitle>
      <DetailsCard title={userData?.name}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2
          }}
        >
          {!isEditingContactInfo ? (
            <Box
              sx={{
                ...dashBorder,
                display: 'flex',
                p: 3,
                flexDirection: 'column',
                flex: '1 1 0px'
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography variant="h6" color="grey.900">
                  Contact Information
                </Typography>
                <IconButton
                  onClick={() =>
                    isTalent
                      ? setOpenWarningModal(true)
                      : setIsEditingContactInfo(true)
                  }
                >
                  {isTalent ? (
                    <HelpOutlineIcon sx={{ cursor: 'pointer' }} />
                  ) : (
                    <EditIcon sx={{ cursor: 'pointer' }} />
                  )}
                </IconButton>
              </Box>
              <TwoColumnsText
                data={profileDataLeft}
                thStyle={{ width: '120px' }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                ...dashBorder,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 0px'
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography variant="h6" color="grey.900">
                  Contact Information
                </Typography>
                <IconButton onClick={handleClose}>
                  <CloseIcon sx={{ cursor: 'pointer' }} />
                </IconButton>
              </Box>
              <Box display="flex" gap={14}>
                <Stack spacing={3} sx={{ flexShrink: 0 }}>
                  {ProfileDataCols.map((col) => (
                    <BoldText key={col}>{col}</BoldText>
                  ))}
                </Stack>
                <FormProvider {...methods}>
                  <form>
                    <ContactInformationForm />
                  </form>
                </FormProvider>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  my: 3
                }}
              >
                <Button
                  onClick={handleSubmit((data) => onSubmit(data))}
                  sx={{ width: '150px', mr: 7 }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              ...dashBorder,
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 0px'
            }}
          >
            <Typography variant="h6" color="grey.900" mb={2}>
              Login Credentials
            </Typography>

            <TwoColumnsText
              data={profileDataRight}
              thStyle={{ width: '110px' }}
            />
            <Button
              variant="lightBlue"
              sx={{ width: '250px', mt: 3 }}
              onClick={() => setOpenChangePassModal(true)}
            >
              Change Password
            </Button>
          </Box>
        </Box>
      </DetailsCard>
      <ChangePassword
        openModal={openChangePassModal}
        closeModal={() => setOpenChangePassModal(false)}
      />

      <DefaultModal
        open={openWarningModal}
        handleClose={() => setOpenWarningModal(false)}
      >
        <Box sx={{ px: 12, textAlign: 'center' }}>
          <Typography
            variant="inherit"
            textAlign="center"
            color="#645C5A"
            mb={6}
          >
            To update this contact information, please contact the AllWork
            support team at
            <LinkStyle href="tel:1-844-624-4400"> 1-844-624-4400 </LinkStyle>
            or
            <LinkStyle href="mailto:support@allworknow.com">
              {' '}
              support@allworknow.com{' '}
            </LinkStyle>
          </Typography>
        </Box>
      </DefaultModal>

      <SuccessModal open={openSuccessModal} handleClose={closeSuccessModal}>
        <Box sx={{ px: 3 }}>
          <Typography variant="h5" textAlign="center" color="grey.900">
            Profile has been updated
          </Typography>
        </Box>
      </SuccessModal>
    </>
  );
};

export default Profile;
